import React, { useState, memo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Bell, User, LogOut, Settings, Check, AlertTriangle, Calendar } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useAdminAuth } from '@/shared/hooks/useAdminAuth';
import { useTrialDays } from '@/shared/hooks/useTrialDays';
import { User as UserType, Tenant, UserRole } from '@/shared/types';
import { rutaDeNotificacion } from '../notification-route';
import { cn } from '@/shared/utils/cn';
import { getRoleDisplayName } from '@/shared/utils/role-utils';
import { RootState } from '@/shared/store/store';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  toggleDropdown,
  closeDropdown,
  fetchNotificationsRequest,
  fetchStatsRequest,
  markAsReadRequest,
  markAllAsReadRequest,
  deleteNotificationRequest,
  NotificationItem,
  Notification,
  useNotificationToaster,
} from '@/features/notifications';
import { SiteSwitcherDropdown } from '@/features/multi-site';
import { useGlobalNotifications } from '@/shared/contexts/NotificationContext';

export interface HeaderProps {
  onMenuClick: () => void;
  onDesktopMenuClick?: () => void;
  showMenuButton: boolean;
  user: UserType | null;
  tenant: Tenant | null;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  onDesktopMenuClick,
  showMenuButton,
  user,
  tenant,
  className,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Usar el hook de autenticación correcto según la ruta
  const authUser = useAuth();
  const adminUser = useAdminAuth();
  const { logout } = isAdminRoute ? adminUser : authUser;
  const trialDaysInfo = useTrialDays();
  const { showError } = useGlobalNotifications();

  const [showUserMenu, setShowUserMenu] = useState(false);

  // Redux state para notificaciones
  const {
    notifications,
    stats,
    showDropdown,
    loading,
    wsConnected,
    wsError,
    lastFetchTime,
  } = useSelector((state: RootState) => state.notifications);

  // Hook para mostrar toasters cuando se reciban notificaciones
  useNotificationToaster();

  // Debug de notificaciones (solo en entorno de desarrollo)
  const notificationsDebugEnabled = import.meta.env.DEV;

  useEffect(() => {
    if (!notificationsDebugEnabled) return;
    }, [notifications, loading, showDropdown, wsConnected, wsError, notificationsDebugEnabled]);

  useEffect(() => {
    if (!notificationsDebugEnabled) return;
  }, [stats, notificationsDebugEnabled]);

  const handleLogout = async () => {
    try {
      await logout();
      // Redirigir según el tipo de usuario
      if (isAdminRoute) {
        navigate('/admin/login');
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('[Header] Logout failed:', error);
      showError(
        'No se pudo cerrar sesión',
        'Ocurrió un error al cerrar sesión. Intenta de nuevo.'
      );
    }
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.notifications-dropdown') && !target.closest('.notifications-button')) {
        dispatch(closeDropdown());
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown, dispatch]);

  // Handlers para notificaciones
  const handleToggleNotifications = () => {
    if (notificationsDebugEnabled) {
      }

    const nextState = !showDropdown;

    if (nextState) {
      const now = Date.now();
      const shouldRefresh =
        !notifications ||
        notifications.length === 0 ||
        !lastFetchTime ||
        now - lastFetchTime > 60_000;

      if (shouldRefresh) {
        dispatch(fetchNotificationsRequest(undefined));
        dispatch(fetchStatsRequest());
      }
    }

    dispatch(toggleDropdown());
  };

  const handleMarkAsRead = (notificationId: string) => {
    if (notificationsDebugEnabled) {
    }
    dispatch(markAsReadRequest([notificationId]));
  };

  // GH#2683 — NO hay `handleMarkAsUnread`: el API no tiene cómo marcar una notificación como no leída (el cliente
  // `markAsUnread` es un stub que no llama a nada) y el botón solo cambiaba el estado local, que se perdía al recargar.
  // Decisión de la CTO: esconderlo hasta que exista el endpoint (GH#2684). `NotificationItem` solo pinta el botón si
  // recibe `onMarkAsUnread`; cuando haya endpoint, se vuelve a pasar y se quita la guarda de Header.no-leido.gh2683.test.tsx.

  const handleMarkAllAsRead = () => {
    if (notificationsDebugEnabled) {
    }
    dispatch(markAllAsReadRequest());
  };

  const handleDeleteNotification = (notificationId: string) => {
    if (notificationsDebugEnabled) {
    }
    dispatch(deleteNotificationRequest(notificationId));
  };

  const handleNavigateToNotification = (notification: Notification) => {
    if (notificationsDebugEnabled) {
    }
    // GH#1641 — antes navegaba a rutas SIN el prefijo del rol (/leads/:id, etc.)
    // que no están declaradas → 404. Ahora el destino se resuelve por rol→prefijo
    // (rutaDeNotificacion) y solo se navega si la ruta existe; si no hay destino
    // (ej. 'sale', sin ruta de ventas), se marca leída y no se navega.
    if (notification.entityType && notification.entityId) {
      const ruta = rutaDeNotificacion(
        notification.entityType,
        notification.entityId,
        authUser.user?.role as UserRole | undefined,
      );
      if (ruta) {
        navigate(ruta);
      } else {
        dispatch(markAsReadRequest([notification.id]));
      }
    }
    dispatch(closeDropdown());
  };

  return (
    <header
      className={cn(
        'bg-white border-b border-gray-200 px-4 lg:px-6 w-full h-16',
        className
      )}
    >
      <div className="flex items-center justify-between w-full h-full">
        {/* Lado izquierdo */}
        <div className="flex items-center space-x-4">
          {/* Botón de menú para móvil */}
          {showMenuButton && (
            <button
              type="button"
              onClick={onMenuClick}
              aria-label="Abrir menú"
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 cursor-pointer"
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          )}

          {/* Botón de menú para desktop */}
          {showMenuButton && onDesktopMenuClick && (
            <button
              type="button"
              onClick={onDesktopMenuClick}
              aria-label="Mostrar u ocultar menú lateral"
              className="hidden lg:block p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 cursor-pointer"
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          )}

          {/* Logo y nombre del tenant (solo en móvil o cuando no hay botón de menú) */}
          {(!showMenuButton || !onDesktopMenuClick) && (
            <div className="flex items-center space-x-3">
              {tenant?.logo && (
                <img
                  src={tenant.logo}
                  alt={tenant.name}
                  className="h-8 w-8 rounded"
                />
              )}
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {tenant?.name || 'InboMarket'}
                </h1>
                {tenant?.subdomain && (
                  <p className="text-sm text-gray-500">
                    {tenant.subdomain}.inbomarket.com
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Lado derecho */}
        <div className="flex items-center space-x-4">
          {/* #708 Multi-Site switcher — solo mostrar en rutas tenant (no admin) */}
          {!isAdminRoute && tenant && <SiteSwitcherDropdown />}

          {/* Notificaciones — GH#1863: la campana está SIEMPRE. Antes solo
              aparecía si ya había notificaciones, y el dropdown ya tiene su
              estado vacío («No hay notificaciones»). */}
          <div className="relative">
              <button
                type="button"
                onClick={handleToggleNotifications}
                aria-label={
                  stats && stats.unread > 0
                    ? `Notificaciones, ${stats.unread} sin leer`
                    : 'Notificaciones'
                }
                aria-expanded={showDropdown}
                aria-haspopup="true"
                className="notifications-button p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 relative"
              >
                <Bell className="h-6 w-6" aria-hidden="true" />
                {/* Badge de notificaciones (el número ya va en el aria-label) */}
                {stats && stats.unread > 0 && (
                  <span aria-hidden="true" className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {stats.unread > 99 ? '99+' : stats.unread}
                  </span>
                )}
                {/* Indicador de conexión WebSocket */}
                <div className={cn(
                  "absolute -bottom-1 -right-1 h-2 w-2 rounded-full",
                  wsConnected ? "bg-green-500" : "bg-gray-400"
                )} />
              </button>

            {/* Dropdown de notificaciones */}
            {showDropdown && (
              <div className="notifications-dropdown absolute right-0 top-full w-[calc(100vw-1rem)] md:w-[420px] bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Notificaciones
                    </h3>
                    <div className="flex items-center space-x-3">
                      {stats && stats.unread > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                          <Check className="h-4 w-4" />
                          <span>Marcar todas como leídas</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contenido */}
                <div className="max-h-[400px] overflow-y-auto">
                  {loading && (!notifications || notifications.length === 0) ? (
                    <div className="p-8 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                      <p className="text-sm">Cargando notificaciones...</p>
                    </div>
                  ) : !notifications || notifications.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-medium">No hay notificaciones</p>
                      <p className="text-xs text-gray-400 mt-1">Te notificaremos cuando tengas nuevas actualizaciones</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {loading && (
                        <div className="px-4 py-2 text-center text-xs text-gray-400 bg-gray-50">
                          Actualizando notificaciones...
                        </div>
                      )}
                      {notifications.slice(0, 10).map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={handleMarkAsRead}
                          onDelete={handleDeleteNotification}
                          onNavigate={handleNavigateToNotification}
                          className="mx-1 my-0.5"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                {notifications && notifications.length > 10 && (
                  <div className="p-4 border-t border-gray-100 bg-gray-50">
                    <button
                      onClick={() => navigate('/notificaciones')}
                      className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors py-2 px-4 rounded-lg hover:bg-blue-50"
                    >
                      Ver todas las notificaciones ({notifications.length})
                    </button>
                  </div>
                )}

                {/* Error de conexión */}
                {wsError && (
                  <div className="p-4 border-t border-gray-100 bg-yellow-50">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      <p className="text-sm text-yellow-800">
                        Error de conexión: {wsError}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Usuario */}
          <div className="relative">
            <button
              type="button"
              onClick={toggleUserMenu}
              aria-expanded={showUserMenu}
              className="flex items-center space-x-3 p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            >
              {/* GH#1863 (residuo CAC, 390 px): en móvil el texto de abajo es
                  `hidden` y el botón quedaba sin nombre. Este span da el nombre
                  sólo en móvil; en desktop manda el texto visible (WCAG 2.5.3),
                  por eso no se usa aria-label (lo taparía). */}
              <span className="sr-only md:hidden">
                {`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}, menú de usuario
              </span>
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt=""
                  className="h-8 w-8 rounded-full"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600" aria-hidden="true" />
                </div>
              )}
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {getRoleDisplayName(user?.role)}
                </p>
                {/* Badge de días restantes de prueba */}
                {trialDaysInfo.isInTrial && trialDaysInfo.daysRemaining > 0 && (
                  <div className="mt-1.5 space-y-1">
                    <Badge 
                      variant={trialDaysInfo.daysRemaining <= 3 ? 'error' : trialDaysInfo.daysRemaining <= 5 ? 'warning' : 'primary'}
                      size="sm"
                      className="flex items-center gap-1 w-fit"
                    >
                      <Calendar className="h-3 w-3" />
                      <span className="font-semibold">
                        Te quedan {trialDaysInfo.daysRemaining} {trialDaysInfo.daysRemaining === 1 ? 'día' : 'días'} de prueba
                      </span>
                    </Badge>
                    <div className="w-24">
                      <Progress 
                        value={trialDaysInfo.progressPercentage} 
                        className="h-1.5"
                      />
                    </div>
                  </div>
                )}
              </div>
            </button>

            {/* Dropdown del usuario */}
            {showUserMenu && (
              <div className="absolute right-0 top-full w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate(isAdminRoute ? '/admin/profile' : '/profile');
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <User className="h-4 w-4 mr-3" />
                    Mi Perfil
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      // TODO: Navegar a configuración
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Settings className="h-4 w-4 mr-3" />
                    Configuración
                  </button>
                  <hr className="my-1 border-gray-400" />
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      handleLogout();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay para cerrar dropdowns */}
      {(showUserMenu || showDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            dispatch(closeDropdown());
          }}
        />
      )}
    </header>
  );
};

// Optimización con React.memo para evitar re-renders innecesarios
const OptimizedHeader = memo(Header);

OptimizedHeader.displayName = 'OptimizedHeader';

export default OptimizedHeader;
